import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Footer from "../components/HomePage/footer";
import Header from "../components/HomePage/header";

export default function AuthenticationPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });
  const [loading, setLoading] = useState(false); // To control loading state
  const [error, setError] = useState(""); // To handle error messages
  const [resendMessage, setResendMessage] = useState(""); // To handle resend messages

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Sending Form data to Backend
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); // Start loading spinner
    setError(""); // Clear previous error messages
    setResendMessage(""); // Clear previous resend messages

    try {
      const response = await fetch("/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      setLoading(false); // Stop loading spinner

      // Check response status
      if (!response.ok) {
        const errorResponse = await response.json(); // Parse JSON error details, if any
        if (response.status === 400) {
          setError(errorResponse?.message || "Username or password cannot be empty.");
        } else if (response.status === 401) {
          setError("Invalid username or password. Please try again.");
        } else if (response.status === 403) {
          setError(errorResponse?.message || "Access forbidden. Please contact support.");
        } else if (response.status === 500) {
          setError(errorResponse?.message || "An internal server error occurred. Please try again later.");
        } else {
          setError("An error occurred. Please try again.");
        }
      } else {
        const data = await response.json();
        if (data.success) {
          // Login successful, navigate to dashboard
          navigate("/dashboard");
        } else {
          setError("Login failed. Please try again.");
        }
      }
    } catch (error) {
      setLoading(false); // Stop loading spinner
      console.error("Error:", error);
      setError("An error occurred. Please try again.");
    }
  };

  // Resend Verification Email
  const handleResendVerification = async () => {
    try {
      const response = await fetch("/api/resend-verification", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ username: formData.username }),
      });

      const data = await response.json();
      if (response.ok) {
        setResendMessage(data.message || "Verification email has been resent. Please check your inbox.");
      } else {
        setResendMessage(data.message || "Failed to resend verification email. Please try again.");
      }
    } catch (error) {
      console.error("Error during resending verification email:", error);
      setResendMessage("An error occurred while resending verification email. Please try again.");
    }
  };

  return (
    <>
      <section className="relative flex min-h-screen flex-wrap bg-white md:bg-slate-100 lg:items-center">
        <div className="max-w-full mx-auto mt-20 px-20 py-6 pb-32 sm:px-8 sm:py-16 md:pb-32 lg:px-8 lg:pb-32 md:bg-slate-50 md:shadow-md rounded-md">
          <a href="/">
            <img src="./image/prep2.png" width="260" className="mx-auto py-6" />
          </a>
          <div className="mx-auto max-w-lg text-center">
            <h1 className="text-xl font-semibold text-gray-700 sm:text-3xl md:text-xl md:px-10">Login to your account</h1>
          </div>

          <form onSubmit={handleSubmit} className="mx-auto mb-0 mt-8 max-w-sm space-y-4">
            <div>
              <label htmlFor="email" className="sr-only">Email</label>
              <div className="relative">
                <input
                  type="email"
                  id="email"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  className="w-full rounded-lg border-gray-200 p-4 pe-12 text-sm shadow-sm"
                  placeholder="Enter email"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="sr-only">Password</label>
              <div className="relative">
                <input
                  type="password"
                  id="Password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className="w-full rounded-lg border-gray-200 p-4 pe-12 text-sm shadow-sm"
                  placeholder="Enter password"
                />
              </div>
              <p className="pl-48 text-sm text-gray-500">
                <a className="underline" href="/forgotpassword">Forgot password?</a>
              </p>
            </div>

            {error && (
              <div className="text-red-500 text-sm mt-2">
                {error}
                {error === "Please verify your email to log in." && (
                  <div>
                    <button
                      onClick={handleResendVerification}
                      type="button"
                      className="text-blue-500 underline ml-2"
                    >
                      Click here to resend
                    </button>
                  </div>
                )}
              </div>
            )}

            {resendMessage && <div className="text-green-500 text-sm mt-2">{resendMessage}</div>}

            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">
                No account? <a className="underline" href="/signup">Sign up</a>
              </p>

              <button
                type="submit"
                className={`inline-block rounded-sm bg-gray-800 px-5 py-3 text-sm font-medium text-white ${loading ? 'cursor-wait' : ''}`}
                disabled={loading}
              >
                {loading ? (
                  <div className="spinner-border animate-spin w-6 h-6 border-4 border-t-4 border-gray-100 border-solid rounded-full"></div>
                ) : (
                  "Sign in"
                )}
              </button>
            </div>
          </form>
          <p className="mt-10 py-10 px-10 border-t-2 text-gray-600 underline text-sm text-center">Powered by PrepNexis.AI</p>
        </div>
      </section>
    </>
  );
}
